import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, KeyOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { Admin } from '../types';
import TelegramIdEditor from '../components/TelegramIdEditor';

const { Option } = Select;
const { Title } = Typography;

const AdminsManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [telegramIdModalVisible, setTelegramIdModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const currentAdmin = JSON.parse(localStorage.getItem('admin') || '{}');

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAll();
      setAdmins(response.data);
    } catch (error: any) {
      message.error('获取管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentAdmin.permission_level === 2) {
      fetchAdmins();
    }
  }, []);

  const handleAdd = async (values: any) => {
    try {
      await adminAPI.create({
        telegramId: values.telegramId ? parseInt(values.telegramId) : null,
        loginUsername: values.loginUsername,
        displayName: values.displayName,
        password: values.password,
        permissionLevel: values.permissionLevel,
      });

      message.success('管理员添加成功！新管理员可登录后自行绑定 2FA。');
      setModalVisible(false);
      form.resetFields();
      fetchAdmins();
    } catch (error: any) {
      message.error(error.response?.data?.error || '添加失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminAPI.delete(id);
      message.success('管理员已删除');
      fetchAdmins();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleUpdatePermission = async (id: number, permissionLevel: number) => {
    try {
      await adminAPI.updatePermission(id, permissionLevel);
      message.success('权限更新成功');
      fetchAdmins();
    } catch (error: any) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  const handleUpdateStatus = async (id: number, status: boolean) => {
    try {
      await adminAPI.updateStatus(id, status);
      message.success('状态更新成功');
      fetchAdmins();
    } catch (error: any) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  // 打开修改密码弹窗
  const openChangePasswordModal = (admin: Admin) => {
    setEditingAdmin(admin);
    setPasswordModalVisible(true);
    passwordForm.resetFields();
  };

  // 修改密码
  const handleChangePassword = async (values: any) => {
    if (!editingAdmin) return;

    try {
      const isSelf = editingAdmin.id === currentAdmin.id;
      const data = isSelf
        ? { oldPassword: values.oldPassword, newPassword: values.newPassword }
        : { newPassword: values.newPassword };

      await adminAPI.changePassword(editingAdmin.id, data);
      message.success('密码修改成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      setEditingAdmin(null);
    } catch (error: any) {
      message.error(error.response?.data?.error || '修改密码失败');
    }
  };

  if (currentAdmin.permission_level !== 2) {
    return (
      <div>
        <Title level={3}>管理员管理</Title>
        <p>只有超级管理员才能访问此页面</p>
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: 'Telegram ID',
      dataIndex: 'telegram_id',
      width: 180,
      render: (telegramId: number | null, record: Admin) => (
        <Space>
          <span>{telegramId || '未设置'}</span>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingAdmin(record);
              setTelegramIdModalVisible(true);
            }}
          >
            修改
          </Button>
        </Space>
      ),
    },
    {
      title: '显示名称',
      dataIndex: 'username',
      width: 120,
    },
    {
      title: '登录用户名',
      dataIndex: 'login_username',
      width: 120,
    },
    {
      title: '权限等级',
      dataIndex: 'permission_level',
      width: 150,
      render: (level: number, record: Admin) => (
        <Select
          value={level}
          style={{ width: 120 }}
          onChange={(value) => handleUpdatePermission(record.id, value)}
          disabled={record.id === currentAdmin.id}
        >
          <Option value={1}>
            <Tag color="blue">普通管理员</Tag>
          </Option>
          <Option value={2}>
            <Tag color="red">超级管理员</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (status: number, record: Admin) => (
        <Select
          value={status}
          style={{ width: 100 }}
          onChange={(value) => handleUpdateStatus(record.id, value === 1)}
          disabled={record.id === currentAdmin.id}
        >
          <Option value={1}>
            <Tag color="success">启用</Tag>
          </Option>
          <Option value={0}>
            <Tag color="default">禁用</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 180,
      render: (_: any, record: Admin) => (
        <Space size="small">
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => openChangePasswordModal(record)}
          >
            改密码
          </Button>
          <Popconfirm
            title="确定删除此管理员吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.id === currentAdmin.id}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.id === currentAdmin.id}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={3}>管理员管理</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchAdmins}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            添加管理员
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={admins}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="添加管理员"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            label="登录用户名"
            name="loginUsername"
            rules={[
              { required: true, message: '请输入登录用户名' },
              { pattern: /^[a-zA-Z0-9_-]{3,20}$/, message: '3-20位字母数字下划线' },
            ]}
          >
            <Input placeholder="admin001" />
          </Form.Item>

          <Form.Item
            label="显示名称"
            name="displayName"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="张三" />
          </Form.Item>

          <Form.Item
            label="Telegram ID（可选）"
            name="telegramId"
            rules={[
              { pattern: /^\d+$/, message: '请输入有效的数字 ID' },
            ]}
          >
            <Input placeholder="123456789（可留空）" />
          </Form.Item>

          <Form.Item
            label="登录密码"
            name="password"
            rules={[
              { required: true, message: '请输入登录密码' },
              { min: 6, message: '密码长度至少 6 位' },
            ]}
          >
            <Input.Password placeholder="请输入登录密码（至少6位）" />
          </Form.Item>

          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入密码" />
          </Form.Item>

          <Form.Item label="权限等级" name="permissionLevel" initialValue={1}>
            <Select>
              <Option value={1}>普通管理员</Option>
              <Option value={2}>超级管理员</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {editingAdmin && (
        <TelegramIdEditor
          visible={telegramIdModalVisible}
          adminId={editingAdmin.id}
          currentTelegramId={editingAdmin.telegram_id || null}
          onClose={() => {
            setTelegramIdModalVisible(false);
            setEditingAdmin(null);
          }}
          onSuccess={() => {
            fetchAdmins();
          }}
        />
      )}

      {/* 修改密码弹窗 */}
      <Modal
        title={`修改密码 - ${editingAdmin?.username || ''}`}
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          setEditingAdmin(null);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          onFinish={handleChangePassword}
          layout="vertical"
        >
          {editingAdmin && editingAdmin.id === currentAdmin.id && (
            <Form.Item
              label="旧密码"
              name="oldPassword"
              rules={[{ required: true, message: '请输入旧密码' }]}
            >
              <Input.Password placeholder="请输入旧密码" />
            </Form.Item>
          )}

          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少 6 位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>

          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
              <Button
                onClick={() => {
                  setPasswordModalVisible(false);
                  setEditingAdmin(null);
                  passwordForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminsManagement;
